/** Normalize RD availability flags from resume / signup payloads. */
import { updateRegistrationData } from "./registrationSetupStorage";

const NO_PROVIDER_ROUTE = "/register/account/no-rd-available";

const isFalsyApiFlag = (value) =>
  value === false || value === 0 || value === "0" || value === "false";

export const isRdUnavailable = (payload) =>
  payload?.is_rd_available === false ||
  payload?.is_rd_available === 0 ||
  payload?.provider_available === false ||
  payload?.provider_available === 0 ||
  payload?.provider_available === "false" ||
  payload?.provider_available === "0";

/**
 * Resolve provider_available from an API response or stored payload.
 * Mirrors iOS: data["provider_available"].exists()
 */
export const getProviderAvailableFlag = (payload) => {
  if (payload == null || typeof payload !== "object") {
    return { exists: false, value: undefined };
  }

  const candidates = [];
  if (payload.data && typeof payload.data === "object") {
    candidates.push(payload.data);
    if (
      payload.data.registration_progress &&
      typeof payload.data.registration_progress === "object"
    ) {
      candidates.push(payload.data.registration_progress);
    }
  }
  candidates.push(payload);
  if (
    payload.registration_progress &&
    typeof payload.registration_progress === "object"
  ) {
    candidates.push(payload.registration_progress);
  }

  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === "object" &&
      Object.prototype.hasOwnProperty.call(candidate, "provider_available")
    ) {
      return { exists: true, value: candidate.provider_available };
    }
  }

  return { exists: false, value: undefined };
};

/**
 * iOS: if data["provider_available"].exists(), data["provider_available"].boolValue == false
 */
export const shouldGoToNoProviderScreen = (payload) => {
  const { exists, value } = getProviderAvailableFlag(payload);
  return exists && isFalsyApiFlag(value);
};

export const buildRdAvailabilityFromProviderPayload = (payload = {}) => {
  const data =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;

  return {
    is_rd_available: false,
    provider_available: false,
    provider_availability_checked: true,
    state_id: data?.state_id ?? null,
    state_name: data?.state_name ?? null,
    state_code: data?.state_code ?? null,
    reference_number:
      data?.reference_number ??
      data?.reference_no ??
      data?.referenceNumber ??
      null,
  };
};

/** Persist unavailable state and navigate to the no-RD screen. */
export const goToNoProviderAvailableScreen = (history, payload = {}) => {
  const rdAvailability = buildRdAvailabilityFromProviderPayload(payload);
  updateRegistrationData({ rdAvailability });

  if (!history) {
    return rdAvailability;
  }

  if (typeof history.replace === "function") {
    history.replace(NO_PROVIDER_ROUTE, { rdAvailability });
    return rdAvailability;
  }

  if (typeof history.push === "function") {
    history.push(NO_PROVIDER_ROUTE, { rdAvailability });
  }

  return rdAvailability;
};

/**
 * Build a payload from stored registration data for a pre-API provider check.
 */
export const getStoredProviderAvailabilityPayload = (
  registrationData = {}
) => {
  const sources = [
    registrationData.rdAvailability,
    registrationData.emailCodeCheck,
    registrationData.emailCheckResponse,
    registrationData.savePaymentTypeResponse,
    registrationData.insuranceEligibilityResult,
  ];

  for (const source of sources) {
    if (shouldGoToNoProviderScreen({ data: source }) || shouldGoToNoProviderScreen(source)) {
      return source && typeof source === "object" ? { data: source } : {};
    }
  }

  for (const source of sources) {
    const flag = getProviderAvailableFlag({ data: source });
    if (flag.exists) {
      return source && typeof source === "object" ? { data: source } : {};
    }
  }

  return {};
};

/**
 * Guard used before calling provider-sensitive registration APIs.
 * Returns true when navigation to the no-provider screen already happened.
 */
export const redirectIfProviderUnavailableBeforeApi = (
  history,
  registrationData = {}
) => {
  const storedPayload = getStoredProviderAvailabilityPayload(registrationData);
  if (!shouldGoToNoProviderScreen(storedPayload)) {
    return false;
  }
  goToNoProviderAvailableScreen(history, storedPayload);
  return true;
};

/**
 * Guard used after provider-sensitive registration API responses.
 * Returns true when navigation to the no-provider screen already happened.
 */
export const redirectIfProviderUnavailableFromResponse = (history, response) => {
  if (!shouldGoToNoProviderScreen(response)) {
    return false;
  }
  goToNoProviderAvailableScreen(history, response);
  return true;
};

const getListMatchedRdItems = (response) => {
  const data = response?.data;
  return Array.isArray(data) ? data : [];
};

const shouldGoToNoProviderScreenFromSuccessfulEmptyList = (response, getItems) => {
  if (shouldGoToNoProviderScreen(response)) {
    return true;
  }
  if (!response?.status) {
    return false;
  }
  return getItems(response).length === 0;
};

/**
 * list-matched-rd: provider_available === false OR an empty RD list.
 */
export const shouldGoToNoProviderScreenFromListMatchedRd = (response) =>
  shouldGoToNoProviderScreenFromSuccessfulEmptyList(
    response,
    getListMatchedRdItems
  );

/**
 * list-rd-timeslot: only provider_available === false (empty timeslot list is allowed).
 */
export const shouldGoToNoProviderScreenFromListRdTimeslot = (response) =>
  shouldGoToNoProviderScreen(response);

/**
 * Guard after list-matched-rd responses.
 * Returns true when navigation to the no-provider screen already happened.
 */
export const redirectIfNoProviderFromListMatchedRdResponse = (history, response) => {
  if (!shouldGoToNoProviderScreenFromListMatchedRd(response)) {
    return false;
  }
  goToNoProviderAvailableScreen(history, response);
  return true;
};

/**
 * Guard after list-rd-timeslot responses.
 * Returns true when navigation to the no-provider screen already happened.
 */
export const redirectIfNoProviderFromListRdTimeslotResponse = (history, response) => {
  if (!shouldGoToNoProviderScreenFromListRdTimeslot(response)) {
    return false;
  }
  goToNoProviderAvailableScreen(history, response);
  return true;
};
